import { useMemo } from 'react';

import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';

import { WHILE_YOU_WAIT_WIDGET } from '#client/constants/experimentsFlags';

import { ALLOWED_ORDER_TASKS, CHIP_COLORS, ORDER_TASKS_UPSELLS, WFC_ADVANCED_UPSELL } from './constants';

// Entitlement type constants
const ENTITLEMENT_WFC = 'worry_free_service';
const ENTITLEMENT_WFC_BASIC = 'worry_free_service_basic';
const ENTITLEMENT_WFC_ADVANCED = 'worry_free_service_advanced';

// Banking application statuses that should hide the banking task from OrderTracker
const FINAL_BANKING_STATUSES = ['approved', 'denied', 'canceled', 'expired', 'not_qualified', 'closed'];

/**
 * Check if we should fetch banking application status for this task
 * @param {object} item - Order task item
 * @returns {boolean} Whether to fetch banking application status
 */
export function shouldFetchBankingStatus(item) {
  return item.type === 'zb_banking_preferred' && item.status === 'pending';
}

/**
 * Check if a banking task should be hidden from OrderTracker
 * @param {object} item - Order task item
 * @param {string} bankingApplicationStatus - Banking application status
 * @returns {boolean} Whether to hide this banking task
 */
export function shouldHideBankingTask(item, bankingApplicationStatus) {
  if (!bankingApplicationStatus) {
    return false;
  }
  return item.type === 'zb_banking_preferred' && FINAL_BANKING_STATUSES.includes(bankingApplicationStatus);
}

export function getTimelineItems(orderTasks) {
  const validStatuses = Object.keys(CHIP_COLORS);
  return orderTasks
    .filter((item) => ALLOWED_ORDER_TASKS.includes(item.type) && validStatuses.includes(item.status))
    .map((item) => ({
      ...item,
      color: CHIP_COLORS[item.status],
    }))
    .sort((a, b) => ALLOWED_ORDER_TASKS.indexOf(a.type) - ALLOWED_ORDER_TASKS.indexOf(b.type));
}

export function getUpsellItems(type, entitlements, jurisdictionCode) {
  if (!entitlements) {
    return [];
  }

  const typeUpsells = ORDER_TASKS_UPSELLS[type] || [];
  const upsellsToProcess = [...typeUpsells]; // Create a copy to work with

  // Special handling for worry-free service upsells
  const hasWorryfreeService = entitlements.has(ENTITLEMENT_WFC) || entitlements.has(ENTITLEMENT_WFC_BASIC);
  const hasWorryfreeAdvanced = entitlements.has(ENTITLEMENT_WFC_ADVANCED);

  // If user has worry_free_service but not worry_free_advanced, replace WFC with WFC Advanced
  if (hasWorryfreeService && !hasWorryfreeAdvanced) {
    const wfcIndex = upsellsToProcess.findIndex((upsell) => upsell.entitlement === ENTITLEMENT_WFC);
    if (wfcIndex !== -1) {
      // Replace WFC with WFC Advanced at the same position
      upsellsToProcess[wfcIndex] = WFC_ADVANCED_UPSELL;
    }
  }

  // Now filter out upsells the user already has
  let filteredUpsells = upsellsToProcess.filter((upsell) => !entitlements.has(upsell.entitlement));

  // Filter out upsells that are not allowed for the jurisdiction
  filteredUpsells = filteredUpsells.filter((upsell) => {
    if (upsell.allowedJurisdictions) {
      return upsell.allowedJurisdictions.includes(jurisdictionCode);
    }
    return true;
  });

  return filteredUpsells;
}

export function getTitleMessageSuffix(item) {
  if (item.type === 'change_of_registered_agent' && item.jurisdictionCode) {
    return ` - ${item.jurisdictionCode}`;
  }
  return '';
}

/** Default A/B variation when flag is off or not set */
export const WHILE_YOU_WAIT_VARIANT_CONTROL = 'control';
/** Variant value that means the While You Wait widget should be shown */
export const WHILE_YOU_WAIT_VARIANT_SHOW_WIDGET = 'while_you_wait';

/**
 * Hook for While You Wait test variation from LaunchDarkly.
 * Use a string flag in LaunchDarkly with variations e.g. "control", "while_you_wait".
 * @returns {string} Variation key (e.g. 'control' | 'while_you_wait')
 */
export function useWhileYouWaitABVariant() {
  const variant = useExperimentFlag(WHILE_YOU_WAIT_WIDGET, WHILE_YOU_WAIT_VARIANT_CONTROL);

  return useMemo(() => (typeof variant === 'string' ? variant : WHILE_YOU_WAIT_VARIANT_CONTROL), [variant]);
}

/**
 * Hook to retrieve whether the While You Wait widget should be shown.
 * Returns true when the variant is "while_you_wait"
 * @returns {boolean} Whether the While You Wait widget is enabled for this user
 */
export function useShowWhileYouWaitWidget() {
  const abVariant = useWhileYouWaitABVariant();

  return useMemo(() => abVariant === WHILE_YOU_WAIT_VARIANT_SHOW_WIDGET, [abVariant]);
}
